import React from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Avatar,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  Save as SaveIcon,
  CameraAlt as CameraAltIcon
} from '@mui/icons-material';

import ApiService from '../services/api.service';
import { getBrandColor, getBrandContrastText, resolveImageUrl } from '../utils/colorUtils';
import { isPlatformLevel } from '../utils/roleUtils';

// 13px fields, like the rest of the app.
const FIELD_SX = {
  '& .MuiInputBase-root, & .MuiInputLabel-root': { fontSize: '0.8125rem' },
};

const MyProfile = ({ currentUser }) => {
  const theme = useTheme();
  const visualScope = isPlatformLevel(currentUser?.role) ? 'platform' : 'tenant';
  const brandColor = getBrandColor(theme, visualScope);
  const brandContrastText = getBrandContrastText(theme, visualScope);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');

  const [uploadingPhoto, setUploadingPhoto] = React.useState(false);
  const [profileImageUrl, setProfileImageUrl] = React.useState(currentUser?.profileImageUrl || '');

  // Last loaded/saved values — Save is enabled only when something differs.
  const [savedProfile, setSavedProfile] = React.useState(null);
  const [profile, setProfile] = React.useState(() => ({
    fullName: currentUser?.name || '',
    email: currentUser?.email || '',
    username: currentUser?.username || '',
    typingCenterName: currentUser?.typingCenterName || '',
    typingCenterCode: ''
  }));

  React.useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await ApiService.get('/api/users/me');
        const data = res?.data?.data ?? res?.data;
        if (!mounted) return;

        const normalized = {
          fullName: data?.fullName ?? data?.full_name ?? '',
          username: data?.username ?? '',
          email: data?.email ?? '',
          typingCenterName: data?.typingCenterName ?? data?.typing_center_name ?? '',
          typingCenterCode: data?.typingCenterCode ?? data?.typing_center_code ?? '',
          profileImageUrl: data?.profileImageUrl ?? data?.profile_image_url ?? ''
        };

        const loaded = {
          fullName: normalized.fullName,
          username: normalized.username,
          email: normalized.email,
          typingCenterName: normalized.typingCenterName,
          typingCenterCode: normalized.typingCenterCode
        };
        setProfile(loaded);
        setSavedProfile(loaded);
        if (normalized.profileImageUrl) {
          setProfileImageUrl(normalized.profileImageUrl);
        }
      } catch (e) {
        if (!mounted) return;
        setError(e?.response?.data?.message || 'Failed to load profile');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [currentUser]);

  const onSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        username: profile.username,
        fullName: profile.fullName,
        email: profile.email
      };

      const res = await ApiService.put('/api/users/me', payload);
      const data = res?.data?.data ?? res?.data;

      const normalized = {
        fullName: data?.fullName ?? data?.full_name,
        username: data?.username,
        email: data?.email,
        typingCenterName: data?.typingCenterName ?? data?.typing_center_name,
        typingCenterCode: data?.typingCenterCode ?? data?.typing_center_code
      };

      setSavedProfile({ ...profile, fullName: normalized.fullName ?? profile.fullName, username: normalized.username ?? profile.username, email: normalized.email ?? profile.email });
      setProfile((p) => ({
        ...p,
        fullName: normalized.fullName ?? p.fullName,
        username: normalized.username ?? p.username,
        email: normalized.email ?? p.email,
        typingCenterName: normalized.typingCenterName ?? p.typingCenterName,
        typingCenterCode: normalized.typingCenterCode ?? p.typingCenterCode
      }));

      try {
        const stored = localStorage.getItem('currentUser');
        const obj = stored ? JSON.parse(stored) : {};
        localStorage.setItem(
          'currentUser',
          JSON.stringify({
            ...obj,
            name: normalized.fullName ?? profile.fullName,
            username: normalized.username ?? profile.username,
            email: normalized.email ?? profile.email,
            typingCenterName: normalized.typingCenterName ?? profile.typingCenterName
          })
        );
      } catch {}

      setSuccess('Profile updated');
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be under 2MB');
      return;
    }
    setUploadingPhoto(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await ApiService.post('/api/users/me/profile-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const data = res?.data?.data ?? res?.data;
      const newUrl = data?.profileImageUrl ?? data?.profile_image_url ?? '';
      if (newUrl) {
        setProfileImageUrl(newUrl);
        try {
          const stored = localStorage.getItem('currentUser');
          const obj = stored ? JSON.parse(stored) : {};
          localStorage.setItem('currentUser', JSON.stringify({ ...obj, profileImageUrl: newUrl }));
        } catch {}
        setSuccess('Profile photo updated');
      }
    } catch (e) {
      setError('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const initial = (profile.fullName || profile.username || 'U').charAt(0).toUpperCase();
  const dirty = !savedProfile || ['fullName', 'username', 'email'].some((k) => (profile[k] || '') !== (savedProfile[k] || ''));
  const set = (key) => (e) => setProfile((p) => ({ ...p, [key]: e.target.value }));

  return (
    <Box sx={{ height: '100%', minWidth: 0 }}>
      <Paper variant="outlined" sx={{ p: 2, height: '100%', minWidth: 0, ...FIELD_SX }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Profile</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 2 }}>Your name and the details you sign in with.</Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {/* Who: photo, name, organisation, change photo */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Tooltip title={uploadingPhoto ? 'Uploading…' : 'Change photo (JPG or PNG, max 2 MB)'}>
                <Box component="label" sx={{ position: 'relative', cursor: uploadingPhoto ? 'default' : 'pointer', flexShrink: 0 }}>
                  <Avatar
                    src={resolveImageUrl(profileImageUrl) || undefined}
                    alt={profile.fullName}
                    sx={{ width: 56, height: 56, fontSize: '1.375rem', bgcolor: brandColor, color: brandContrastText }}
                  >
                    {initial}
                  </Avatar>
                  <Box sx={{
                    position: 'absolute', right: -2, bottom: -2, width: 22, height: 22, borderRadius: '50%',
                    bgcolor: 'background.paper', border: 1, borderColor: 'divider', display: 'grid', placeItems: 'center',
                  }}>
                    {uploadingPhoto ? <CircularProgress size={12} /> : <CameraAltIcon sx={{ fontSize: 13, color: 'text.secondary' }} />}
                  </Box>
                  <input hidden type="file" accept="image/*" onChange={handlePhotoUpload} disabled={uploadingPhoto} />
                </Box>
              </Tooltip>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }} noWrap>{profile.fullName || profile.username}</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }} noWrap>
                  {[profile.typingCenterName, profile.typingCenterCode].filter(Boolean).join(' · ') || profile.email}
                </Typography>
              </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Full name" value={profile.fullName} onChange={set('fullName')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Username" value={profile.username} onChange={set('username')} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" type="email" label="Email" value={profile.email} onChange={set('email')} />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="contained" size="small" onClick={onSave} disabled={saving || !dirty}
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none', fontSize: '0.8125rem' }}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </Box>
          </>
        )}
      </Paper>

      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ fontSize: '0.8125rem' }}>{success}</Alert>
      </Snackbar>
    </Box>
  );
};

export default MyProfile;
